import type { TicketDetail, TicketKbDraft } from '@epc/contracts';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BookPlus } from 'lucide-react';
import { ApiError, api } from '../../lib/api';
import { Button } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

/** Начало системной реплики, которой портал отмечает уже созданный черновик (как на сервере, kb-draft.ts) */
const MARK = 'Черновик статьи базы знаний';

/**
 * Статья из закрытого обращения (БЗ-26): специалист одним нажатием отправляет на проверку черновик «Вопрос — ответ»
 * в раздел базы знаний «Ответы поддержки», чтобы следующий такой вопрос решался без человека.
 */
export function KbDraftPanel({ t, onDone }: { t: TicketDetail; onDone: () => void }) {
  const draft = useMutation({
    mutationFn: () => api<TicketKbDraft>(`/support/tickets/${t.id}/kb-draft`, { method: 'POST' }),
    onSuccess: (d) => {
      toast(`Черновик ${d.number} отправлен на проверку`);
      onDone();
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не удалось подготовить статью', 'danger'),
  });
  if (!t.canManage || (t.status !== 'closed' && t.status !== 'answered')) return null;
  const done = t.messages.find((m) => m.kind === 'system' && m.body.startsWith(MARK));
  return (
    <Panel title="Статья в базу знаний">
      {done || draft.data ? (
        <p className="text-[14px] leading-6">
          {done ? done.body : `${MARK} ${draft.data!.number} отправлен на проверку`}.{' '}
          {draft.data ? (
            <Link
              to="/kb/articles/$id"
              params={{ id: draft.data.articleId }}
              className="inline-flex min-h-11 items-center text-accent underline md:min-h-0"
            >
              Открыть черновик
            </Link>
          ) : null}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-[14px] leading-6 text-ink-2">
            Похожий вопрос может появиться снова. Подготовьте статью «Вопрос — ответ» из этой переписки: её
            проверит и опубликует владелец раздела «Ответы поддержки». Суммы, личные данные и имена в статью
            не попадут.
          </p>
          <Button
            icon={<BookPlus className="size-4" />}
            loading={draft.isPending}
            onClick={() => draft.mutate()}
            className="self-start"
          >
            Подготовить статью
          </Button>
        </div>
      )}
    </Panel>
  );
}
